import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@root/environments/environment';
import { map, Observable } from 'rxjs';
import { DiscountFilters } from '../store/discount.state';
import {
  CreateDiscountPayload,
  Discount,
  DiscountApiModel,
  DiscountApiModelData,
  DiscountList,
} from '../types/pricing.types';
import { DiscountUtilsService } from './discount-utils.service';

@Injectable({
  providedIn: 'root',
})
export class DiscountService {
  private http = inject(HttpClient);
  private discountUtilsService = inject(DiscountUtilsService);

  private readonly API_URL = `${environment.apiUrl}/nipt-invoicing/discounts`;

  public createDiscount(
    discount: CreateDiscountPayload
  ): Observable<Discount[]> {
    return this.http
      .post<
        DiscountApiModelData[]
      >(`${this.API_URL}`, this.discountUtilsService.discountToApiModel(discount))
      .pipe(
        map((discounts: DiscountApiModelData[]) =>
          discounts.map((discount) =>
            this.discountUtilsService.apiModelToDiscount(discount)
          )
        )
      );
  }

  public updateDiscount(discount: Discount): Observable<Discount> {
    return this.http
      .put<DiscountApiModelData>(
        `${this.API_URL}/${discount.id}`,
        this.discountUtilsService.discountToApiModel(discount)
      )
      .pipe(
        map((model: DiscountApiModelData) =>
          this.discountUtilsService.apiModelToDiscount(model)
        )
      );
  }

  public getDiscount(id: string): Observable<Discount> {
    return this.http
      .get<DiscountApiModelData>(`${this.API_URL}/${id}`)
      .pipe(
        map((model: DiscountApiModelData) =>
          this.discountUtilsService.apiModelToDiscount(model)
        )
      );
  }

  public getDiscountsList(filters: DiscountFilters): Observable<DiscountList> {
    return this.http
      .get<DiscountApiModel>(`${this.API_URL}`, {
        params: this.discountUtilsService.buildDiscountHttpParams(filters),
      })
      .pipe(
        map((model: DiscountApiModel) => {
          return {
            data: model.data.map((model: DiscountApiModelData) =>
              this.discountUtilsService.apiModelToDiscount(model)
            ),
            totalCount: model.total_count,
            hasMorePages: model.has_more_pages,
            perPage: model.per_page,
            currentPage: model.current_page,
          };
        })
      );
  }

  public activateDiscount(id: string): Observable<Discount> {
    return this.http
      .patch<DiscountApiModelData>(`${this.API_URL}/${id}/approve`, {})
      .pipe(
        map((model: DiscountApiModelData) =>
          this.discountUtilsService.apiModelToDiscount(model)
        )
      );
  }

  public deleteDiscount(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`);
  }
}
